import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { generatePath } from 'react-router-dom';
import {
  Container,
  Grid,
  Heading,
  Divider,
  ButtonLink,
  ButtonEnums,
  OnlyDesktop,
  OnlyMobileCSS,
} from 'components/atoms';
import { SearchRadioToggle, DateFilter } from 'components/molecules';
import { ReactComponent as ArrowBack } from 'resources/images/ic-arrow-back.svg';
import { FilterIds, FilterIdToNameMap, Routes } from 'constants/index';
import { getPath } from 'utils';
import { Contentful } from 'mxp-schemas';
import { WebcastPassPopup, WebcastPassPosition } from '../PageProduct/WebcastPassPopup';

interface AggregationEventsProps {
  productType: Contentful.ProductTypes.ProductType;
  webcastPassFilterItems: number;
  webcastPassFilterSelectedItems: string[];
  eventDateFilterItems: Common.FilterItemProps[];
  eventStartDateSelectedItems: string[];
  eventEndDateSelectedItems: string[];
  webcastPass: State.ContentCardItem | null;
  changeFilter: (name: string, value: string) => void;
}

export const AggregationEvents: React.FC<AggregationEventsProps> = ({
  productType,
  webcastPassFilterItems,
  webcastPassFilterSelectedItems,
  eventDateFilterItems,
  eventStartDateSelectedItems,
  eventEndDateSelectedItems,
  webcastPass,
  changeFilter,
}) => {
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(moment());
  const to: string = generatePath(getPath(Routes.STOREFRONT_PAGE));

  const isWebcastPassPublished = Boolean(webcastPass?.published);

  return (
    <Wrapper>
      <Container>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <StyledButtonLink
                testId="back-button"
                to={to}
                variant={ButtonEnums.variants.link}
                size={ButtonEnums.sizes.small}
                icon={<ArrowBack />}
                iconPosition={ButtonEnums.iconPosition.left}
              >
                Go to CPE &amp; Learning
              </StyledButtonLink>
              <StyledHeading as="h1">{productType?.title}</StyledHeading>
              <StyledParagraph>{productType?.summary}</StyledParagraph>
              <StyledDivider />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <StyledSearchRadioToggleGridColumn>
                <StyledMonthHolder>
                  {currentMonth.format('MMMM YYYY')} - {currentMonth.clone().add(2, 'month').format('MMMM YYYY')}
                </StyledMonthHolder>
                {isWebcastPassPublished && (
                  <StyledContainer>
                    <StyledSearchRadioToggle
                      filterName={FilterIdToNameMap[FilterIds.webcastPass]}
                      handleChange={changeFilter}
                      value={webcastPassFilterSelectedItems && webcastPassFilterSelectedItems[0]}
                      disabled={!webcastPassFilterItems}
                    />
                    <StyledLabel>
                      Included in
                      <OnlyDesktop>
                        <WebcastPassPopup position={WebcastPassPosition.DESKTOPTOGGLE} isToggle />
                      </OnlyDesktop>
                      <OnlyMobileCSS>
                        <WebcastPassPopup position={WebcastPassPosition.MOBILETOGGLE} isToggle />
                      </OnlyMobileCSS>
                    </StyledLabel>
                  </StyledContainer>
                )}
              </StyledSearchRadioToggleGridColumn>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <DateFilter
                items={eventDateFilterItems}
                selectedStartDate={eventStartDateSelectedItems?.[0]}
                selectedEndDate={eventEndDateSelectedItems?.[0]}
                handleChange={changeFilter}
                onMonthChanged={setCurrentMonth}
                numberOfMonths={3}
                hideTitle
                hideLegend
                eventsPage
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </Wrapper>
  );
};

const StyledContainer = styled.div`
  display: flex;
`;

const StyledLabel = styled.label`
  display: flex;
  font-weight: ${props => props.theme.fontWeights.light};
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.neutralWhite} !important;
  line-height: 1.29;
  letter-spacing: ${props => props.theme.pxToRem(0.25)};
`;

const Wrapper = styled.div`
  padding-top: ${props => props.theme.pxToRem(40)};
  padding-bottom: ${props => props.theme.pxToRem(40)};
  box-shadow: inset 0 0 ${props => props.theme.pxToRem(10)} 0 ${props => props.theme.colors.neutralGrey3};
  background-image: linear-gradient(60deg, #672d89, #9c2463 99%);

  ${props => props.theme.mediaQueries.mobileOnly} {
    padding-top: ${props => props.theme.pxToRem(24)};
    background-image: linear-gradient(27deg, #672d89, #9c2463 96%);
  }
`;

const StyledButtonLink = styled(ButtonLink)`
  &&&& {
    color: ${props => props.theme.colors.neutralWhite};
    font-weight: ${props => props.theme.fontWeights.light};
    line-height: 1.57;

    svg path {
      fill: ${props => props.theme.colors.neutralWhite};
    }
  }
`;

const StyledHeading = styled(Heading)`
  margin-top: ${props => props.theme.pxToRem(27)};
  margin-bottom: ${props => props.theme.pxToRem(8)};
  color: ${props => props.theme.colors.neutralWhite};
  font-size: ${props => props.theme.fontSizes.xxl};
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: 1.35;

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.xl};
    line-height: 1.38;
  }
`;

const StyledParagraph = styled.p`
  max-width: ${props => props.theme.pxToRem(645)};
  color: ${props => props.theme.colors.neutralWhite};
  font-size: ${props => props.theme.fontSizes.m};
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: 1.44;

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.s};
    line-height: 1.5;
  }
`;

const StyledDivider = styled(Divider)`
  &&&&& {
    border-top-color: transparent;
    border-bottom-color: ${props => props.theme.colors.neutralWhite};
    margin-top: ${props => props.theme.pxToRem(24)};
    margin-bottom: ${props => props.theme.pxToRem(24)};

    ${props => props.theme.mediaQueries.mobileOnly} {
      margin-bottom: 0;
    }
  }
`;

const StyledSearchRadioToggleGridColumn = styled.div`
  &&&& {
    display: flex;
    justify-content: space-between;

    ${props => props.theme.mediaQueries.mobileOnly} {
      text-align: left;
      justify-content: flex-start;
    }
  }
`;

const StyledMonthHolder = styled.div`
  color: ${props => props.theme.colors.neutralWhite};
  margin: 6px 0 0 25px;
  width: 225px;
  text-align: center;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};

  ${props => props.theme.mediaQueries.mobileOnly} {
    display: none;
  }
`;

const StyledSearchRadioToggle = styled(SearchRadioToggle)`
  &&&& {
    div:first-child {
      margin: 0;

      &.ui.toggle.checkbox label,
      &&&.ui.toggle.checkbox.checked label {
        padding-left: ${props => props.theme.pxToRem(48)};
        color: ${props => props.theme.colors.neutralWhite} !important;
        line-height: 1.29;
        letter-spacing: ${props => props.theme.pxToRem(0.25)};
      }
    }
  }
`;
